import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction, i18n as I18nType } from "i18next";

import { useNotificationProvider, ThemedLayoutV2 } from "@refinedev/antd";
import {
  Action,
  Authenticated,
  ErrorComponent,
  IResourceItem,
  Refine,
} from "@refinedev/core";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
} from "@refinedev/react-router-v6";
import { AvisoDeCambiosSinGuardar } from "@/components/unsaved-changes";

import { App as AntdApp, ConfigProvider } from "antd";
import type { Locale } from "antd/es/locale";
import datePickerEs from "antd/es/date-picker/locale/es_ES";
import "dayjs/locale/es";
import { installNumberPaste } from "@/utilities/paste-numbers";
import { xconsTheme } from "@/config/xconsTheme";
import { Header } from "@/components/layout/header";
import { Resources, usePerfilDelMenu } from "@/config/resources";
import { authProvider } from "@/services/auth";
import { SmartMenuProvider } from "@/config/context/smartMenuContext";
import { xconsDataProvider } from "@/providers";
import { CustomSider } from "@/components/xcons";
import "@/config/config.css";

import {
  HealthCheck,
  DashboardPage,
  LoginPage,
  Orders,
  SellersList,
  SellersCreate,
  SellersShow,
  AsociadorPage,
  StoresList,
  StoresCreate,
  SellersUserPermissionsList,
  SellersUserPermissionsPanel,
  ContactsList,
  ContactDetails,
  ConversationsPage,
  ChannelsPage,
  ChannelDetail,
  StoresUserPermissionsList,
  StoresUserPermissionsPanel,
  StoresTemplatesList,
  StoresTemplatesPanel,
  Quotes,
  QuoteDetail,
  OrderDetails,
  LinkedSellerList,
  LinkedSellerUserPermissionsPanel,
  LinkedStoreList,
  LinkedStoreUserPermissionsPanel,
  ShippingConfigList,
  PaymentConfigList,
  SellerSettingsList,
  StoresSettingsList,
  SellerOptimizerConfigList,
  StoreOptimizerConfigList,
  StoreAgentPage,
} from "@/pages";
import {
  CatalogList,
  CatalogDetailPage,
  PendingReviewPage,
  CatalogAttributesList,
  CatalogAttributeSetsList,
  CatalogAttributeSetEditor,
  StoreCatalogPage,
  CatalogImportPage,
  ProductFormPage,
} from "@/pages/catalog";
import { SoloConSeccionDeAtributos } from "@/pages/catalog/attributes/seccion-de-atributos";
import { NotificationsPage } from "@/pages/notifications";

import "@refinedev/antd/dist/reset.css";
import { SelectedStoresProvider } from "./config/context/SelectedItemsContext";
import { NotFound } from "./pages/error404";
import { PageConstruction } from "./pages/error404/page-construction";
import { StoresPage } from "./pages/error404/stores";
import { SellersPage } from "./pages/error404/sellers";
import { useEffect } from "react";
import ActivepiecesEmbed from "./pages/stores/automations";

// `Modal.confirm` y compañía se renderizan fuera del árbol de React, así que no ven el
// `ConfigProvider` de abajo: sin esto su botón primario sale en el azul default de antd y
// no en el verde de la marca. Aplica a todos los modales estáticos del admin.
/**
 * Los calendarios en español en todo el admin (xcons-docs#610): meses, días, "Ahora",
 * "Aceptar" y el placeholder. Sólo los pickers: el resto de los componentes de antd sigue
 * con sus textos de siempre, porque pasarlos todos a es_ES cambiaría textos que nadie pidió.
 * El placeholder dice "Seleccionar día" y no el "Seleccionar fecha" de es_ES.
 */
const pickerEs = {
  ...datePickerEs,
  lang: {
    ...datePickerEs.lang,
    placeholder: "Seleccionar día",
    dateTimePlaceholder: "Seleccionar día",
  },
};
const antdLocale: Locale = {
  locale: "es",
  DatePicker: pickerEs,
  TimePicker: datePickerEs.timePickerLocale,
  Calendar: pickerEs,
};

ConfigProvider.config({
  holderRender: (children) => (
    <ConfigProvider theme={{ ...xconsTheme }} locale={antdLocale}>{children}</ConfigProvider>
  ),
});

interface CustomTitleHandlerProps {
  resource?: { label: string } | IResourceItem;
  action?: Action;
  params?: Record<string, string | undefined>;
  pathname?: string;
  autoGeneratedTitle: string;
}

const App = () => {
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage("es");
  }, [i18n]);

  // Pegar `$ 1.500,50` en cualquier input numérico deja 1500,50 y no 1,5005.
  useEffect(() => installNumberPaste(), []);

  // El perfil para el menú. Va por el authProvider y no por `useGetIdentity`: acá estamos
  // FUERA de `<Refine>`, así que no hay QueryClient — ver `usePerfilDelMenu`.
  const identidadDelMenu = usePerfilDelMenu();

  const i18nProvider = {
    translate: (key: string, params?: Record<string, any>) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const customTitleHandler = ({
    resource,
    action,
    params,
  }: CustomTitleHandlerProps): string => {
    let title = "";

    if (resource) {
      title = `${"XCONS Admin"} ${
        resource.label ? `| ${t(resource.label)}` : ""
      } ${params?.id ? `- ${params.id.toUpperCase()}` : ""}`;
    }

    return title;
  };

  return (
    <BrowserRouter>
      <SelectedStoresProvider>
        <SmartMenuProvider>
          <ConfigProvider theme={{ ...xconsTheme }} locale={antdLocale}>
            <AntdApp>
              <Refine
                i18nProvider={i18nProvider}
                routerProvider={routerProvider}
                dataProvider={xconsDataProvider}
                notificationProvider={useNotificationProvider}
                authProvider={authProvider}
                resources={Resources(t, identidadDelMenu)}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  liveMode: "auto",
                  useNewQueryKeys: true,
                }}
              >
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-layout"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <ThemedLayoutV2 Header={Header} Sider={CustomSider}>
                          <Outlet />
                        </ThemedLayoutV2>
                      </Authenticated>
                    }
                  >
                    <Route path="*" element={<PageConstruction />} />

                    <Route path="/">
                      <Route index element={<DashboardPage />} />
                    </Route>

                    <Route path="/contacts">
                      <Route index element={<ContactsList />} />
                      <Route
                        path="/contacts/:id"
                        element={<ContactDetails />}
                      />
                    </Route>

                    <Route path="/conversations">
                      <Route index element={<ConversationsPage />} />
                    </Route>

                    <Route path="/notifications">
                      <Route index element={<NotificationsPage />} />
                    </Route>

                    <Route path="/quotes">
                      <Route index element={<Quotes />} />
                      <Route path=":id" element={<QuoteDetail />} />
                    </Route>

                    <Route path="/orders">
                      <Route index element={<Orders />} />
                      <Route path="/orders/:id" element={<OrderDetails />} />
                    </Route>

                    <Route path="/reports">
                      <Route index element={<PageConstruction />} />
                    </Route>

                    <Route path="/catalog">
                      <Route index element={<CatalogList />} />
                      {/* La revisión va por el id de la versión pendiente y NO por SKU: un
                          alta todavía no existe en Magento, así que no hay producto que
                          abrir por SKU. Antes de `:sku` para que `pending` no se lea como
                          uno. */}
                      <Route path="pending/:id" element={<PendingReviewPage />} />
                      {/* Editar un borrador o una pendiente: se identifica por el id de la
                          versión, no por SKU — un borrador no existe en Magento. */}
                      <Route path="pending/:id/edit" element={<ProductFormPage />} />
                      {/* ABM de la metadata del EAV (Fase 16). Van antes de `:sku` por lo
                          mismo que `pending`: los segmentos estáticos ganan, así que un
                          producto cuyo SKU fuera "attributes" no se abriría por URL. */}
                      <Route path="import" element={<CatalogImportPage />} />
                      {/* El form. `nuevo` es el alta: va por el mismo componente porque el
                          form se arma del conjunto en los dos casos. */}
                      <Route path=":sku/edit" element={<ProductFormPage />} />
                      {/* Atributos y Conjuntos, sólo con su permiso aparte (xcons-docs#478). */}
                      <Route
                        path="attributes"
                        element={
                          <SoloConSeccionDeAtributos>
                            <CatalogAttributesList />
                          </SoloConSeccionDeAtributos>
                        }
                      />
                      <Route
                        path="attribute-sets"
                        element={
                          <SoloConSeccionDeAtributos>
                            <CatalogAttributeSetsList />
                          </SoloConSeccionDeAtributos>
                        }
                      />
                      <Route
                        path="attribute-sets/:id"
                        element={
                          <SoloConSeccionDeAtributos>
                            <CatalogAttributeSetEditor />
                          </SoloConSeccionDeAtributos>
                        }
                      />
                      {/* El SKU es la clave del catálogo, así que la ruta va por SKU y
                          no por id: es lo que hace el link compartible y estable. */}
                      <Route path=":sku" element={<CatalogDetailPage />} />
                    </Route>

                    <Route path="/sellers">
                      <Route index element={<SellersList />} />
                      <Route path="create" element={<SellersCreate />} />

                      <Route path="linked-products" element={<SellersPage />} />

                      <Route
                        path="linked-products/:id"
                        element={<SellersShow />}
                      />
                      {/* El asociador en pantalla propia, por el SKU del seller (xcons-docs#609). */}
                      <Route
                        path="linked-products/:id/:sku"
                        element={<AsociadorPage />}
                      />

                      <Route
                        path="linked-stores/:id"
                        element={<LinkedStoreList />}
                      />

                      <Route
                        path="linked-stores/:id/:id"
                        element={<LinkedStoreUserPermissionsPanel />}
                      />

                      <Route
                        path="operators/:id"
                        element={<SellersUserPermissionsList />}
                      />
                      <Route
                        path="operators/:id/:id"
                        element={<SellersUserPermissionsPanel />}
                      />

                      <Route
                        path="shipping-config/:id"
                        element={<ShippingConfigList />}
                      />

                      <Route
                        path="payment-config/:id"
                        element={<PaymentConfigList />}
                      />

                      <Route
                        path="settings/:id"
                        element={<SellerSettingsList />}
                      />

                      {/* Apps → Optimizer del seller (xcons-docs#524). */}
                      <Route
                        path="optimizer/:id"
                        element={<SellerOptimizerConfigList />}
                      />
                    </Route>

                    <Route path="/stores">
                      <Route index element={<StoresList />} />
                      <Route path="create" element={<StoresCreate />} />

                      <Route path="linked-sellers" element={<StoresPage />} />

                      <Route
                        path="linked-sellers/:id"
                        element={<LinkedSellerList />}
                      />

                      <Route
                        path="linked-sellers/:id/:id"
                        element={<LinkedSellerUserPermissionsPanel />}
                      />

                      <Route
                        path="operators/:id"
                        element={<StoresUserPermissionsList />}
                      />
                      <Route
                        path="operators/:id/:id"
                        element={<StoresUserPermissionsPanel />}
                      />

                      <Route path="channels/:id" element={<ChannelsPage />} />
                      <Route
                        path="channels/:id/:channelId"
                        element={<ChannelDetail />}
                      />

                      <Route
                        path="stores-settings/:id"
                        element={<StoresSettingsList />}
                      />

                      {/* Apps → Optimizer del store (xcons-docs#524). */}
                      <Route
                        path="optimizer/:id"
                        element={<StoreOptimizerConfigList />}
                      />

                      {/* Asistente IA del store: configuración y conocimiento (xcons-docs#566). */}
                      <Route path="agent/:id" element={<StoreAgentPage />} />

                      {/* Catálogo POR STORE (Fase 18): el otro scope del PIM. Va bajo
                          /stores, en el menú del store, porque su gate es el permiso
                          `categorias` de ESE store y no el `catalog` global. */}
                      <Route
                        path="categories/:id"
                        element={<StoreCatalogPage />}
                      />

                      <Route
                        path="templates/:id"
                        element={<StoresTemplatesList />}
                      />
                      <Route
                        path="templates/:id/:id"
                        element={<StoresTemplatesPanel />}
                      />
                      <Route
                        path="automations/:id"
                        element={<ActivepiecesEmbed />}
                      />
                    </Route>

                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                  <Route path="/health_check" element={<HealthCheck />} />
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-auth"
                        fallback={<Outlet />}
                      >
                        <NavigateToResource resource="dashboard" />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<LoginPage />} />
                  </Route>
                </Routes>
                {/* El popup del admin, no el del navegador (xcons-docs#490). */}
                <AvisoDeCambiosSinGuardar />
                <DocumentTitleHandler handler={customTitleHandler} />
              </Refine>
            </AntdApp>
          </ConfigProvider>
        </SmartMenuProvider>
      </SelectedStoresProvider>
    </BrowserRouter>
  );
};

export default App;
